import React, { useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Flame } from 'lucide-react';

const DAYS = ['Lundi', 'Mardi', 'Jeudi', 'Vendredi', 'Samedi'];

export function slotStatus(s) {
  if (s.reserved >= s.capacity) return { cls: 'bg-[#F7E6E1] border-[#DBB0A4]', dot: 'bg-[#C76542]', text: 'text-[#8A4A3B]', label: 'Complet' };
  if (s.reserved === s.capacity - 1) return { cls: 'bg-[#FBF3DC] border-[#DCC488]', dot: 'bg-[#C99A55]', text: 'text-[#9A744C]', label: 'Presque complet' };
  return { cls: 'bg-[#EDF2EA] border-[#BCC9B6]', dot: 'bg-[#8C9B86]', text: 'text-[#5C7057]', label: 'Disponible' };
}

function Cell({ s, onSelect, testPrefix }) {
  const st = slotStatus(s);
  return (
    <button
      onClick={() => onSelect(s)}
      className={`w-full text-left rounded-xl border ${st.cls} p-2.5 transition-transform hover:scale-[1.02] hover:shadow-sm`}
      data-testid={`${testPrefix}-cell-${s.id}`}
    >
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold text-[#493329]">{s.start} – {s.end}</span>
        {s.highlight && <Flame className="w-3 h-3 text-[#C99A55]" />}
      </div>
      <div className="text-[10px] text-[#7A6455] truncate">{s.type.replace('Pilates Reformer – ', '')}</div>
      <div className={`text-[10px] font-semibold ${st.text} mt-0.5 flex items-center gap-1`}>
        <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
        {s.reserved}/{s.capacity}{s.waitlist_count > 0 ? ` · ${s.waitlist_count} att.` : ''}
      </div>
    </button>
  );
}

export function GridLegend() {
  return (
    <div className="flex flex-wrap items-center gap-4 text-[11px] text-[#7A6455]">
      <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#8C9B86]" /> Disponible</span>
      <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#C99A55]" /> Presque complet</span>
      <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#C76542]" /> Complet</span>
    </div>
  );
}

export default function PlanningGrid({ slots, onSelect, testPrefix = 'grid' }) {
  const times = [...new Set(slots.map((s) => s.start))].sort();
  const find = (day, t) => slots.find((s) => s.day === day && s.start === t);
  const [dayIdx, setDayIdx] = useState(0);
  const scrollRef = useRef(null);

  const goTo = (i) => {
    const idx = Math.max(0, Math.min(DAYS.length - 1, i));
    setDayIdx(idx);
    const el = scrollRef.current?.children[idx];
    if (el) el.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  };

  const onScroll = (e) => {
    const el = e.currentTarget;
    const i = Math.round(el.scrollLeft / el.clientWidth);
    if (i !== dayIdx && i >= 0 && i < DAYS.length) setDayIdx(i);
  };

  return (
    <div className="space-y-3" data-testid={`${testPrefix}-planning-grid`}>
      <GridLegend />

      {/* Desktop : grille hebdo complète */}
      <div className="hidden md:block overflow-x-auto rounded-2xl border border-[#E9DDCC] bg-white p-3">
        <div className="grid gap-1.5" style={{ gridTemplateColumns: `64px repeat(${DAYS.length}, minmax(120px, 1fr))` }}>
          <div />
          {DAYS.map((d) => (
            <div key={d} className="text-center text-[11px] uppercase tracking-wider font-semibold text-[#493329] py-1.5 bg-[#F5EFE5] rounded-lg">{d}</div>
          ))}
          {times.map((t) => (
            <React.Fragment key={t}>
              <div className="flex items-center justify-center text-[11px] font-semibold text-[#AE9885]">{t}</div>
              {DAYS.map((d) => {
                const s = find(d, t);
                return (
                  <div key={`${d}-${t}`}>
                    {s ? <Cell s={s} onSelect={onSelect} testPrefix={testPrefix} /> : <div className="h-full min-h-[62px] rounded-xl bg-[#F5EFE5]" />}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Mobile : vue jour avec swipe */}
      <div className="md:hidden space-y-3">
        <div className="flex items-center justify-between">
          <button onClick={() => goTo(dayIdx - 1)} disabled={dayIdx === 0} className="p-2 rounded-full bg-white border border-[#E9DDCC] disabled:opacity-30" data-testid={`${testPrefix}-day-prev`}>
            <ChevronLeft className="w-4 h-4 text-[#493329]" />
          </button>
          <div className="flex gap-1.5 overflow-x-auto px-1">
            {DAYS.map((d, i) => (
              <button key={d} onClick={() => goTo(i)}
                className={`px-3 py-1.5 rounded-full text-[11px] whitespace-nowrap transition-colors ${i === dayIdx ? 'bg-[#C99A55] text-white' : 'bg-white border border-[#E9DDCC] text-[#7A6455]'}`}
                data-testid={`${testPrefix}-day-chip-${i}`}>
                {d.slice(0, 3)}
              </button>
            ))}
          </div>
          <button onClick={() => goTo(dayIdx + 1)} disabled={dayIdx === DAYS.length - 1} className="p-2 rounded-full bg-white border border-[#E9DDCC] disabled:opacity-30" data-testid={`${testPrefix}-day-next`}>
            <ChevronRight className="w-4 h-4 text-[#493329]" />
          </button>
        </div>
        <div ref={scrollRef} onScroll={onScroll} className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide" style={{ scrollbarWidth: 'none' }}>
          {DAYS.map((d) => (
            <div key={d} className="w-full shrink-0 snap-center px-0.5">
              <div className="bg-white rounded-2xl border border-[#E9DDCC] p-3 space-y-2">
                <p className="text-[11px] uppercase tracking-wider font-semibold text-[#C99A55]">{d}</p>
                {times.map((t) => {
                  const s = find(d, t);
                  return s ? <Cell key={t} s={s} onSelect={onSelect} testPrefix={`${testPrefix}-m`} /> : null;
                })}
                {times.every((t) => !find(d, t)) && <p className="text-xs text-[#AE9885]">Aucun cours ce jour.</p>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
